import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { ChevronLeft, ChevronRight, Download, RefreshCcw } from "lucide-react";

type DriverRow = { driver_name: string; email: string | null; AdminRights?: string | null };

type DcpRow = {
  id: string;
  taxicaller_job_id: string;
  driver_name: string;
  paid_amount: number;

  pu_date: string | null;
  pu_time: string | null;
  do_time: string | null;

  cash_amt: number | null;
  eftpos_amt: number | null;
  totalmobility_amt: number | null;
  tm_hoist_amt: number | null;
  account_amt: number | null;
  other_amt: number | null;
  ecard_amt: number | null;
  discount: number | null;

  eftpos_valid: boolean | null;
  total_mobility_valid: boolean | null;
  tm_hoist_valid: boolean | null;
  ecard_valid: boolean | null;

  reconciled: boolean | null;
};

type AmountField = {
  key: keyof DcpRow;
  validKey?: keyof DcpRow;
  label: string;
  requiresReconciled?: boolean;
};

const AMOUNT_FIELDS: AmountField[] = [
  { key: "cash_amt", label: "Cash", requiresReconciled: true },
  { key: "eftpos_amt", validKey: "eftpos_valid", label: "EFTPOS" },
  { key: "totalmobility_amt", validKey: "total_mobility_valid", label: "Total Mobility" },
  { key: "tm_hoist_amt", validKey: "tm_hoist_valid", label: "TM Hoist" },
  { key: "discount", label: "Discount", requiresReconciled: true }, // ✅ added here
  { key: "account_amt", label: "Account", requiresReconciled: true },
  { key: "other_amt", label: "Other", requiresReconciled: true },
  { key: "ecard_amt", validKey: "ecard_valid", label: "eCard" },
];

const startOfWeekMon = (d: Date) => {
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1) - day;
  const nd = new Date(d);
  nd.setDate(d.getDate() + diff);
  nd.setHours(0, 0, 0, 0);
  return nd;
};
const endOfWeekSun = (d: Date) => {
  const s = startOfWeekMon(d);
  const e = new Date(s);
  e.setDate(s.getDate() + 6);
  e.setHours(23, 59, 59, 999);
  return e;
};
const fmtISODateLocal = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const addDays = (d: Date, n: number) => {
  const nd = new Date(d);
  nd.setDate(d.getDate() + n);
  return nd;
};
const money = (n: number) => (Math.round((Number(n || 0) + Number.EPSILON) * 100) / 100).toFixed(2);

type DayAgg = { jobs: number; sums: Record<string, number> };
type DriverAgg = { driverName: string; driverEmail: string | null; byDay: Record<string, DayAgg>; total: DayAgg };
const emptyDayAgg = (): DayAgg => ({ jobs: 0, sums: Object.fromEntries(AMOUNT_FIELDS.map(f => [f.label, 0])) });

const RPC_NAME = "get_reconciled_dcp";

const PayoutReport: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) can only pick from their linked driver(s) here -- this report
  // is view/export only (no writes), so there's no separate edit toggle
  // to respect, just who it can be run for. Unrestricted (the default --
  // everyone today) is unaffected.
  const { effectivePermissions } = useAppContext();
  const scopedDriverNames = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedDriverNames;

  const [loading, setLoading] = useState(false);
  const [drivers, setDrivers] = useState<DriverRow[]>([]);
  const [search, setSearch] = useState("");
  const [selectedDriverNames, setSelectedDriverNames] = useState<string[]>([]);
  const [rangeStart, setRangeStart] = useState<Date>(() => startOfWeekMon(new Date()));
  const [rangeEnd, setRangeEnd] = useState<Date>(() => endOfWeekSun(new Date()));
  const [combineWeeks, setCombineWeeks] = useState(false);
  const [rows, setRows] = useState<DcpRow[]>([]);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase
        .from("drivers")
        .select("driver_name,email,\"AdminRights\"")
        .order("driver_name", { ascending: true });
      if (error) {
        console.error(error);
        toast({ title: "Error loading drivers", description: error.message, variant: "destructive" });
        return;
      }
      setDrivers(data || []);
    })();
  }, []);

  const filteredDrivers = useMemo(() => {
    let result = drivers;
    if (scopedDriverNames) {
      const scoped = new Set(scopedDriverNames);
      result = result.filter(d => scoped.has(d.driver_name));
    }
    if (!search.trim()) return result;
    const lc = search.toLowerCase();
    return result.filter(
      d =>
        d.driver_name.toLowerCase().includes(lc) ||
        (d.email && d.email.toLowerCase().includes(lc))
    );
  }, [drivers, search, scopedDriverNames]);

  // A restricted user never has to select their own linked driver(s) --
  // pre-check them as soon as the (already-scoped) list loads.
  useEffect(() => {
    if (scopedDriverNames && scopedDriverNames.length > 0) {
      setSelectedDriverNames(scopedDriverNames);
    }
  }, [scopedDriverNames]);

  const toggleDriver = (name: string) => {
    setSelectedDriverNames(prev =>
      prev.includes(name) ? prev.filter(n => n !== name) : [...prev, name]
    );
  };

  // --- FETCH via RPC (bypasses RLS on the table itself) ---
  const fetchData = async () => {
    if (!selectedDriverNames.length) {
      toast({ title: "No drivers selected", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc(RPC_NAME, {
        p_driver_names: selectedDriverNames,
        p_date_from: fmtISODateLocal(rangeStart),
        p_date_to: fmtISODateLocal(rangeEnd),
      });
      if (error) throw error;

      setRows((Array.isArray(data) ? data : []) as DcpRow[]);
      toast({
        title: "Data loaded",
        description: `${(data as any[])?.length || 0} reconciled jobs found`,
      });
    } catch (e: any) {
      console.error(e);
      toast({ title: "Error fetching data", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  // --- Aggregate by driver/day ---
  const aggregated = useMemo(() => {
    const map = new Map<string, DriverAgg>();

    for (const r of rows) {
      if (!map.has(r.driver_name)) {
        map.set(r.driver_name, {
          driverName: r.driver_name,
          driverEmail: drivers.find(d => d.driver_name === r.driver_name)?.email || null,
          byDay: {},
          total: emptyDayAgg(),
        });
      }
      const agg = map.get(r.driver_name)!;
      const day = r.pu_date || "Unknown";
      if (!agg.byDay[day]) agg.byDay[day] = emptyDayAgg();
      const dayAgg = agg.byDay[day];

      dayAgg.jobs += 1;
      agg.total.jobs += 1;

      for (const f of AMOUNT_FIELDS) {
        if (f.validKey && !r[f.validKey]) continue;
        if (f.requiresReconciled && !r.reconciled) continue;
        const val = Number(r[f.key] || 0);
        dayAgg.sums[f.label] += val;
        agg.total.sums[f.label] += val;
      }
    }

    // sort days for each driver
    for (const a of map.values()) {
      const sorted: Record<string, DayAgg> = {};
      Object.keys(a.byDay)
        .sort()
        .forEach(k => (sorted[k] = a.byDay[k]));
      a.byDay = sorted;
    }

    return Array.from(map.values()).sort((a, b) => a.driverName.localeCompare(b.driverName));
  }, [rows, drivers]);

  const downloadCsv = (csv: string, filename: string) => {
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const exportCSV = () => {
    if (!aggregated.length) return;

    const esc = (s: string) => (s && /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s || "");

    if (combineWeeks) {
      const header = ["Driver", "Email", "Period Start", "Period End", "Jobs", ...AMOUNT_FIELDS.map(f => f.label)];
      let csv = header.join(",") + "\n";
      for (const a of aggregated) {
        csv += [
          esc(a.driverName),
          esc(a.driverEmail || ""),
          fmtISODateLocal(rangeStart),
          fmtISODateLocal(rangeEnd),
          String(a.total.jobs),
          ...AMOUNT_FIELDS.map(f => money(a.total.sums[f.label])),
        ].join(",") + "\n";
      }
      downloadCsv(csv, `payout-report_combined_${fmtISODateLocal(rangeStart)}_${fmtISODateLocal(rangeEnd)}.csv`);
    } else {
      const header = ["Driver", "Email", "Date", "Jobs", ...AMOUNT_FIELDS.map(f => f.label)];
      let csv = header.join(",") + "\n";
      for (const a of aggregated) {
        for (const [day, dayAgg] of Object.entries(a.byDay)) {
          csv += [
            esc(a.driverName),
            esc(a.driverEmail || ""),
            esc(day),
            String(dayAgg.jobs),
            ...AMOUNT_FIELDS.map(f => money(dayAgg.sums[f.label])),
          ].join(",") + "\n";
        }
        csv += [
          esc(a.driverName),
          esc(a.driverEmail || ""),
          "TOTAL",
          String(a.total.jobs),
          ...AMOUNT_FIELDS.map(f => money(a.total.sums[f.label])),
        ].join(",") + "\n";
      }
      downloadCsv(csv, `payout-report_${fmtISODateLocal(rangeStart)}_${fmtISODateLocal(rangeEnd)}.csv`);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader><CardTitle>Payout Report</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {/* driver search + bulk select */}
          <div className="flex gap-2 items-center">
            <Input
              placeholder="Search drivers..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="max-w-xs"
            />
            <Button onClick={() => setSelectedDriverNames(filteredDrivers.map(d => d.driver_name))} size="sm">
              Select All
            </Button>
            <Button onClick={() => setSelectedDriverNames([])} size="sm" variant="outline">
              Clear
            </Button>
          </div>

          {/* driver list */}
          <div className="max-h-60 overflow-y-auto border rounded p-2 space-y-1">
            {filteredDrivers.map(d => (
              <div key={d.driver_name} className="flex items-center gap-2">
                <Checkbox
                  checked={selectedDriverNames.includes(d.driver_name)}
                  onCheckedChange={() => toggleDriver(d.driver_name)}
                />
                <span>{d.driver_name}</span>
              </div>
            ))}
            {!filteredDrivers.length && (
              <div className="text-sm text-muted-foreground p-2">No drivers match your search.</div>
            )}
          </div>

          {/* date range + combine toggle */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex gap-2 items-center">
              <Button
                onClick={() => {
                  setRangeStart(addDays(rangeStart, -7));
                  setRangeEnd(addDays(rangeEnd, -7));
                }}
                size="sm"
              >
                <ChevronLeft />
              </Button>
              <Input
                type="date"
                value={fmtISODateLocal(rangeStart)}
                onChange={e => setRangeStart(new Date(e.target.value))}
                className="w-40"
              />
              <span>to</span>
              <Input
                type="date"
                value={fmtISODateLocal(rangeEnd)}
                onChange={e => setRangeEnd(new Date(e.target.value))}
                className="w-40"
              />
              <Button
                onClick={() => {
                  setRangeStart(addDays(rangeStart, 7));
                  setRangeEnd(addDays(rangeEnd, 7));
                }}
                size="sm"
              >
                <ChevronRight />
              </Button>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={combineWeeks} onCheckedChange={v => setCombineWeeks(Boolean(v))} />
              Combine weeks (treat full range as one payout)
            </label>
          </div>

          {/* actions */}
          <div className="flex gap-2">
            <Button onClick={fetchData} disabled={loading}>
              <RefreshCcw className="mr-2 h-4 w-4" />
              {loading ? "Loading..." : "Load Data"}
            </Button>
            <Button onClick={exportCSV} disabled={!rows.length} variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* results */}
      {(() => {
        const aggregatedList = useMemo(() => aggregated, [aggregated]); // keep TS happy inside IIFE
        return aggregatedList.length > 0 ? (
          <Card>
            <CardHeader><CardTitle>Results</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Driver</TableHead>
                    {!combineWeeks && <TableHead>Date</TableHead>}
                    <TableHead>Jobs</TableHead>
                    {AMOUNT_FIELDS.map(f => (
                      <TableHead key={f.label}>{f.label}</TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aggregatedList.map(agg => {
                    if (combineWeeks) {
                      return (
                        <TableRow key={`combined-${agg.driverName}`}>
                          <TableCell>{agg.driverName}</TableCell>
                          <TableCell>{agg.total.jobs}</TableCell>
                          {AMOUNT_FIELDS.map(f => (
                            <TableCell key={f.label}>${money(agg.total.sums[f.label])}</TableCell>
                          ))}
                        </TableRow>
                      );
                    }
                    return (
                      <React.Fragment key={agg.driverName}>
                        {Object.entries(agg.byDay).map(([day, dayAgg]) => (
                          <TableRow key={`${agg.driverName}-${day}`}>
                            <TableCell>{agg.driverName}</TableCell>
                            <TableCell>{day}</TableCell>
                            <TableCell>{dayAgg.jobs}</TableCell>
                            {AMOUNT_FIELDS.map(f => (
                              <TableCell key={f.label}>${money(dayAgg.sums[f.label])}</TableCell>
                            ))}
                          </TableRow>
                        ))}
                        <TableRow className="font-bold bg-muted/30">
                          <TableCell>{agg.driverName}</TableCell>
                          <TableCell>TOTAL</TableCell>
                          <TableCell>{agg.total.jobs}</TableCell>
                          {AMOUNT_FIELDS.map(f => (
                            <TableCell key={f.label}>${money(agg.total.sums[f.label])}</TableCell>
                          ))}
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : null;
      })()}
    </div>
  );
};

export default PayoutReport;
